"use client";

// Recent articles grid (Figma 8241:21632) — mixed press releases and blog posts
// with client-side pagination. Mirrors the About Us press releases section layout.

import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { ControlledPagination } from "@/components/ui/pagination";
import { formatArticleCardDate } from "@/lib/content/article-helpers";
import { cn } from "@/lib/utils";

export interface RecentArticle {
  id: string;
  title: string;
  href: string;
  tag: string;
  publishDate?: string;
  author?: string;
  thumbnail: { src: string; alt: string } | null;
}

interface RecentArticlesSectionProps {
  title: string;
  viewAllLabel: string;
  viewAllHref: string;
  items: RecentArticle[];
  locale: string;
  labels: {
    author: string;
    pagination: string;
    previousPage: string;
    nextPage: string;
  };
  className?: string;
}

const PAGE_SIZE = 3;

export function RecentArticlesSection({
  title,
  viewAllLabel,
  viewAllHref,
  items,
  locale,
  labels,
  className,
}: RecentArticlesSectionProps) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const visible = items.slice(start, start + PAGE_SIZE);

  return (
    <section
      aria-label={title}
      className={cn(
        // Figma 8241:21632 — grey band + framed white panel (matches About Us press grid).
        "bg-grey-50 md:border-grey-100 px-6 py-8 md:border-t md:px-8 md:py-0 md:pb-20 lg:px-20",
        className
      )}
    >
      <div className="md:border-grey-100 mx-auto w-full max-w-[1280px] md:border-x md:border-b md:bg-white md:p-10 lg:p-20">
        <div className="flex flex-col gap-5 md:gap-8">
          <header className="flex items-center justify-between gap-4 md:gap-10">
            <h2 className="text-ds-heading-h3 md:text-ds-display-sm text-grey-950">
              {title}
            </h2>
            <Button
              size="sm"
              variant="secondary"
              render={<Link href={viewAllHref} />}
            >
              {viewAllLabel}
            </Button>
          </header>

          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 md:gap-y-10 lg:grid-cols-3">
            {visible.map((item) => (
              <ArticleCard
                key={item.id}
                item={item}
                locale={locale}
                authorLabel={labels.author}
              />
            ))}
          </ul>

          {totalPages > 1 ? (
            <ControlledPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              ariaLabel={labels.pagination}
              previousLabel={labels.previousPage}
              nextLabel={labels.nextPage}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ArticleCard({
  item,
  locale,
  authorLabel,
}: {
  item: RecentArticle;
  locale: string;
  authorLabel: string;
}) {
  const metaLabel = item.author
    ? `${authorLabel}: ${item.author}`
    : item.publishDate
      ? formatArticleCardDate(item.publishDate, locale)
      : null;

  return (
    <li>
      <Link
        href={item.href}
        className="group flex flex-col gap-[23px] no-underline"
      >
        <div className="rounded-ds-xl border-grey-100 bg-grey-100 aspect-[285/160] w-full overflow-hidden border">
          {item.thumbnail?.src ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={item.thumbnail.src}
              alt={item.thumbnail.alt}
              className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
              loading="lazy"
              decoding="async"
            />
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="rounded-ds-s bg-grey-100 text-grey-950 inline-flex items-center px-1.5 py-1 text-[12px] leading-none font-medium">
              {item.tag}
            </span>
            {metaLabel ? (
              <span className="text-ds-body-sm text-grey-600 tracking-[-0.28px]">
                {metaLabel}
              </span>
            ) : null}
          </div>
          <h3 className="text-grey-950 text-ds-heading-h5 transition-opacity group-hover:opacity-80">
            {item.title}
          </h3>
        </div>
      </Link>
    </li>
  );
}
